import { Box, Container, Alert, Backdrop } from '@mui/material';
import { Edit as EditIcon, Cancel as CancelIcon, Delete as DeleteIcon } from '@mui/icons-material';

import { RepresentativesSection } from '../../components/representatives-section';
import { DepartmentResponse, Staff } from '../../types';
import {
  ConstructionCompanyDetailForm,
  CustomerDetailForm,
  NoharaRepresentative,
  WitnessCompanyDetailForm,
} from '../../types/company.types';

import { FixedFooter } from '@/components/footer/fixed-footer';
import { useConfirmDialog } from '@/hooks/use-confirm-dialog';
import { useLoadingProgress } from '@/hooks/use-loading-progress';
import { useSnackbar } from '@/hooks/use-snackbar';
import { MESSAGES } from '@/lib/constants';

interface DetailFormProps {
  canUpdate: boolean;
  canDelete: boolean;
  formData: WitnessCompanyDetailForm | ConstructionCompanyDetailForm | CustomerDetailForm;
  originalFormData:
    | WitnessCompanyDetailForm
    | ConstructionCompanyDetailForm
    | CustomerDetailForm
    | null;
  getFieldError: (field: string) => string | undefined;
  isFieldValid: (field: string) => boolean;
  addRepresentative: () => void;
  removeRepresentative: (id: string) => void;
  updateRepresentative: (id: string, field: keyof NoharaRepresentative, value: string) => void;
  getSalesPersonOptions: (departmentId: string) => Staff[];
  canAddRepresentative: boolean;
  departments: DepartmentResponse[];
  departmentsLoading: boolean;
  staffsLoading: boolean;
  isSubmitting: boolean;
  onDelete: () => Promise<void>;
  handleCancel: () => void;
  handleEdit: () => void;
  isSubmitDisabled: boolean;
  handleDepartmentChange: (departmentId: string) => void;
  children: React.ReactNode;
  error: string | null;
}

const DetailForm = ({
  canUpdate,
  canDelete,
  formData,
  originalFormData,
  getFieldError,
  isFieldValid,
  addRepresentative,
  removeRepresentative,
  updateRepresentative,
  getSalesPersonOptions,
  canAddRepresentative,
  departments,
  departmentsLoading,
  staffsLoading,
  handleDepartmentChange,
  isSubmitting,
  onDelete,
  handleCancel,
  handleEdit,
  isSubmitDisabled,
  children,
  error,
}: DetailFormProps) => {
  const { setConfirmDialog } = useConfirmDialog();
  const { setIsLoading } = useLoadingProgress();
  const { setSnackbarMessage } = useSnackbar();
  if (error) {
    return (
      <Container maxWidth='lg' sx={{ py: 4 }}>
        <Alert severity='error' sx={{ mb: 3 }}>
          {error}
        </Alert>
      </Container>
    );
  }

  const deleteCompany = async () => {
    try {
      setIsLoading(true);
      await onDelete();
      setSnackbarMessage(MESSAGES.DELETE_CUSTOMER);
    } catch (error) {
      console.error('Error deleting company:', error);
      const message = '削除に失敗しました。もう一度お試しください。';
      setConfirmDialog({
        title: message,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    setConfirmDialog({
      title: '建設会社情報を削除します。',
      confirmDialog: () => {
        void deleteCompany();
      },
      confirmButtonText: '削除',
      cancelButtonText: 'キャンセル',
    });
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
      {/* Company Information Section */}
      <Box sx={{ position: 'relative' }} inert={!canUpdate ? true : false}>
        {children}

        {/* Representatives Section */}
        <RepresentativesSection
          representatives={formData.representatives}
          originalRepresentative={originalFormData?.representatives}
          isEditMode={!!originalFormData}
          onAddRepresentative={addRepresentative}
          onRemoveRepresentative={removeRepresentative}
          onUpdateRepresentative={updateRepresentative}
          getSalesPersonOptions={getSalesPersonOptions}
          getFieldError={getFieldError}
          isFieldValid={isFieldValid}
          canAddRepresentative={canAddRepresentative}
          departments={departments}
          departmentsLoading={departmentsLoading}
          staffsLoading={staffsLoading}
          onDepartmentChange={handleDepartmentChange}
        />

        {/* Add bottom padding to prevent content from being hidden behind fixed footer */}
        <Box sx={{ height: '100px' }} />

        {/* Backdrop to prevent interaction when canUpdate is false */}
        <Backdrop
          open={!canUpdate}
          sx={{
            position: 'absolute',
            backgroundColor: 'rgba(0, 0, 0, 0)',
            zIndex: 9999,
          }}
        />
      </Box>

      {/* Fixed Action Footer */}
      <FixedFooter
        buttons={[
          {
            label: '削除',
            onClick: handleDelete,
            variant: 'outlined',
            color: 'error',
            startIcon: <DeleteIcon />,
            disabled: isSubmitting || !canDelete,
            sx: {
              color: 'red',
              borderColor: 'red',
              '&:hover': {
                borderColor: 'darkred',
                backgroundColor: 'rgba(255, 0, 0, 0.04)',
              },
            },
          },
          {
            label: 'キャンセル',
            onClick: handleCancel,
            variant: 'outlined',
            startIcon: <CancelIcon />,
            disabled: isSubmitting,
            sx: {
              marginLeft: 'auto',
            },
          },
          {
            label: isSubmitting ? '更新中...' : '更新',
            onClick: handleEdit,
            variant: 'contained',
            startIcon: <EditIcon />,
            disabled: isSubmitDisabled || !canUpdate,
            loading: isSubmitting,
          },
        ]}
        isSubmitting={isSubmitting}
        hasNavigationPanel={false}
      />
    </Box>
  );
};

export default DetailForm;
